import { useCallback, useMemo, useState } from 'react';

export interface IUseSequenceOptions<T> {
  initialIndex?: number;
  canGoTo?: (item: T, index: number) => boolean;
  onChange?: (item: T, index: number) => void;
}

export interface IUseSequenceReturn<T> {
  currentIndex: number;
  currentItem: T;
  canGoNext: boolean;
  canGoPrevious: boolean;
  next: () => void;
  previous: () => void;
  goTo: (index: number) => void;
}

/**
 * Hook for managing navigation through a sequence of items with constraints.
 * Handles wrapping, validation, and change callbacks.
 *
 * @param items - Array of items to navigate through
 * @param options - Configuration options
 * @param options.initialIndex - Starting index (default: 0)
 * @param options.canGoTo - Function to validate if we can navigate to a specific item
 * @param options.onChange - Callback when the current item changes
 * @returns Object with current state and navigation methods
 */
export function useSequence<T>(
  items: T[],
  options: IUseSequenceOptions<T> = {},
): IUseSequenceReturn<T> {
  const { initialIndex = 0, canGoTo, onChange } = options;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const currentItem = useMemo(() => items[currentIndex], [items, currentIndex]);

  const getNextIndex = useCallback(
    (index: number): number => {
      let nextIndex = index + 1;
      if (nextIndex >= items.length) {
        // Wrap to beginning
        nextIndex = 0;
      }
      return nextIndex;
    },
    [items.length],
  );

  const getPreviousIndex = useCallback(
    (index: number): number => {
      let prevIndex = index - 1;
      if (prevIndex < 0) {
        // Wrap to end
        prevIndex = items.length - 1;
      }
      return prevIndex;
    },
    [items.length],
  );

  const canGoNext = useMemo(() => {
    if (items.length === 0) {
      return false;
    }
    const nextIndex = getNextIndex(currentIndex);
    const nextItem = items[nextIndex];
    return canGoTo ? canGoTo(nextItem, nextIndex) : true;
  }, [items, currentIndex, getNextIndex, canGoTo]);

  const canGoPrevious = useMemo(() => {
    if (items.length === 0) {
      return false;
    }
    const prevIndex = getPreviousIndex(currentIndex);
    const prevItem = items[prevIndex];
    return canGoTo ? canGoTo(prevItem, prevIndex) : true;
  }, [items, currentIndex, getPreviousIndex, canGoTo]);

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= items.length) {
        return;
      }

      const item = items[index];
      if (canGoTo && !canGoTo(item, index)) {
        return;
      }

      setCurrentIndex(index);
      onChange?.(item, index);
    },
    [items, canGoTo, onChange],
  );

  const next = useCallback(() => {
    if (!canGoNext) {
      return;
    }
    const nextIndex = getNextIndex(currentIndex);
    goTo(nextIndex);
  }, [canGoNext, currentIndex, getNextIndex, goTo]);

  const previous = useCallback(() => {
    if (!canGoPrevious) {
      return;
    }
    const prevIndex = getPreviousIndex(currentIndex);
    goTo(prevIndex);
  }, [canGoPrevious, currentIndex, getPreviousIndex, goTo]);

  return {
    currentIndex,
    currentItem,
    canGoNext,
    canGoPrevious,
    next,
    previous,
    goTo,
  };
}
